import {
    useState,
    useEffect,
    ChangeEvent,
    Dispatch,
    SetStateAction
} from 'react';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';

import theme from '@styles/theme';
import { Mutation, AdminPermission } from '@graphql/types';
import { UPDATE_ADMIN_PERMISSION } from '@graphql/mutations';
import { SEE_ADMIN_DETAIL } from '@graphql/queries';

import { FlexBox } from '@components/styled/StyledComponents';
import StyledModal from '@components/styled/StyledModal';
import StyledButton from '@components/styled/StyledButton';
import StyledCheckbox from '@components/styled/StyledCheckbox';

type PermissionModalProps = {
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    data: AdminPermission;
};

const PermissionModal = ({ isOpen, setIsOpen, data }: PermissionModalProps) => {
    const [loading, setLoading] = useState(false);
    const [checked, setChecked] = useState({
        apId: 0,
        dashboard: false,
        userTab: false,
        product: false,
        management: false
    });

    const [updateAdminPermission] = useMutation<
        Pick<Mutation, 'updateAdminPermission'>
    >(UPDATE_ADMIN_PERMISSION);

    useEffect(() => {
        if (data) {
            setChecked({
                apId: data.apId as number,
                dashboard: data.dashboard as boolean,
                userTab: data.user as boolean,
                product: data.product as boolean,
                management: data.management as boolean
            });
        }
    }, [data]);

    const onChange = (e: ChangeEvent<HTMLInputElement>, type: string) => {
        const { checked } = e.target;
        if (type === 'dashboard')
            return toast.warning('대시보드는 기본 권한입니다.');

        setChecked((prev) => ({ ...prev, [type]: checked }));
    };

    const onSubmit = async () => {
        const { apId, dashboard, userTab, product, management } = checked;
        setLoading(true);

        try {
            await updateAdminPermission({
                variables: {
                    apId,
                    dashboard,
                    userTab,
                    product,
                    management
                },
                onCompleted: () => {
                    toast.success(`접근 권한을 수정했습니다.`);
                    setIsOpen(false);
                },
                refetchQueries: [SEE_ADMIN_DETAIL]
            });
        } catch (err) {
            console.error(err);
        } finally {
            return setLoading(false);
        }
    };

    return (
        <StyledModal
            title="접근 권한 수정"
            isOpen={isOpen}
            closeBtnHandler={() => setIsOpen(false)}
            marginBottom={32}
        >
            <FlexBox $gap={32} $flexWrap="wrap">
                <StyledCheckbox
                    checkboxId="permission-dashboard"
                    name="permission"
                    value={checked.dashboard}
                    checked={checked.dashboard}
                    label="대시보드"
                    onChange={(e) => onChange(e, 'dashboard')}
                    width="calc((100% - 64px)/3)"
                    fontColor={theme.colors.blackColor}
                />
                <StyledCheckbox
                    checkboxId="permission-user"
                    name="permission"
                    value={checked.userTab}
                    checked={checked.userTab}
                    label="회원"
                    onChange={(e) => onChange(e, 'userTab')}
                    width="calc((100% - 64px)/3)"
                    fontColor={theme.colors.blackColor}
                />
                <StyledCheckbox
                    checkboxId="permission-product"
                    name="permission"
                    value={checked.product}
                    checked={checked.product}
                    label="상품"
                    onChange={(e) => onChange(e, 'product')}
                    width="calc((100% - 64px)/3)"
                    fontColor={theme.colors.blackColor}
                />
                <StyledCheckbox
                    checkboxId="permission-management"
                    name="permission"
                    value={checked.management}
                    checked={checked.management}
                    label="운영"
                    onChange={(e) => onChange(e, 'management')}
                    width="calc((100% - 64px)/3)"
                    fontColor={theme.colors.blackColor}
                />
            </FlexBox>
            <FlexBox $gap={8} $justifyContent="flex-end" $margin="64px 0 0">
                <StyledButton
                    title="취소"
                    width={140}
                    bgColor={theme.colors.whiteColor}
                    fontColor={theme.colors.blackColor}
                    border={`${theme.colors.lightGrayBorder} 1px solid`}
                    onClick={() => setIsOpen(false)}
                />
                <StyledButton
                    title="수정"
                    width={140}
                    bgColor={theme.colors.pointGreen}
                    onClick={onSubmit}
                    loading={loading}
                />
            </FlexBox>
        </StyledModal>
    );
};

export default PermissionModal;
